import { useCallback, useMemo } from "react";

import { isValidAggregation, useGetIncentivesPotentialPayoutsAdmin, useGetIncentivesPotentialPayoutsByFarmId } from "utils";

import { CarbonProgram, IncentiveName, IncentivesPotentialPayouts, SingleFieldOutcomes } from "models";

import {
  CurrentIncentivePayouts,
  aggregateIncentiveCurrentPayouts,
  aggregatePracticeBasedIncentiveCurrentPayouts,
} from "pages/app/field-timeline/components/PracticeBasedImpact";

export interface InventoryImpact {
  projectAreaHa: number;
  maximumIncentivePerHectare: number;
  maximumPotentialPayout: number;
  totalOutcomeBasedPayoutMin: number;
  totalOutcomeBasedPayoutMax: number;
  totalPracticeBasedPayout: number;
  totalPayoutMin: number;
  totalPayoutMax: number;
  isMaxTotalPayoutReached: boolean;
  totalOutcomePotentialPayout: number;
  isMaxOutcomePayoutReached: boolean;
  hideInventoryPayouts: boolean;
  incentivesCurrentPayouts: CurrentIncentivePayouts;
  allPracticesMaxedOut: boolean;
  isPracticePotentialFullyUtilized: boolean;
  practicePotentialPayoutTotal: number;
  incentivesPotentialPayouts: IncentivesPotentialPayouts | undefined;
  displayPerHectareOrTotalFarm: (value: number, isPerHectare: boolean) => number;
}

export function useInventoryImpactAdmin(
  reportingYear: number,
  singleFieldOutcomes: SingleFieldOutcomes[],
  farmId: string,
  carbonProgram: CarbonProgram | undefined
): InventoryImpact {
  const { incentivesPotentialPayouts } = useGetIncentivesPotentialPayoutsAdmin(farmId);

  return useInventoryImpactShared(reportingYear, singleFieldOutcomes, carbonProgram, incentivesPotentialPayouts);
}
export function useInventoryImpact(
  reportingYear: number,
  singleFieldOutcomes: SingleFieldOutcomes[],
  farmId: string,
  carbonProgram: CarbonProgram | undefined
): InventoryImpact {
  const { incentivesPotentialPayouts } = useGetIncentivesPotentialPayoutsByFarmId(carbonProgram ? farmId : undefined);

  return useInventoryImpactShared(reportingYear, singleFieldOutcomes, carbonProgram, incentivesPotentialPayouts);
}

// For lack of a better naming this is the function that is reused for both user and admin hook
function useInventoryImpactShared(
  reportingYear: number,
  singleFieldOutcomes: SingleFieldOutcomes[],
  carbonProgram: CarbonProgram | null | undefined,
  incentivesPotentialPayouts: IncentivesPotentialPayouts | undefined
) {
  const projectAreaHa = incentivesPotentialPayouts?.projectAreaHa ?? 0;
  const practicePotentialPayoutTotal = incentivesPotentialPayouts?.practicePotentialTotalPayout ?? 0;
  const maximumIncentivePerHectare = carbonProgram?.payoutLimitsHa ?? 0;
  const maximumPotentialPayout = maximumIncentivePerHectare * projectAreaHa;

  // per hectare or total payout
  const displayPerHectareOrTotalFarm = useCallback(
    (value: number, isPerHectare: boolean) => {
      if (isPerHectare && projectAreaHa > 0 && Number.isFinite(value)) {
        return value / projectAreaHa;
      }
      return value;
    },
    [projectAreaHa]
  );

  // Outcome-based range
  const { min: uncappedOutcomeMin, max: uncappedOutcomeMax } = useMemo(
    () =>
      singleFieldOutcomes
        .map((field) => field.fieldOutcomes.crops?.years[reportingYear])
        .filter(isValidAggregation)
        .reduce(
          (acc, { payouts }) => ({
            min: acc.min + (payouts?.min ?? 0),
            max: acc.max + (payouts?.max ?? 0),
          }),
          { min: 0, max: 0 }
        ),
    [reportingYear, singleFieldOutcomes]
  );

  const practiceBasedIncentivesCurrentPayouts = useMemo(
    () => aggregatePracticeBasedIncentiveCurrentPayouts(singleFieldOutcomes, incentivesPotentialPayouts),
    [singleFieldOutcomes, incentivesPotentialPayouts]
  );

  const totalPracticeBasedPayout = useMemo(
    () => Object.values(practiceBasedIncentivesCurrentPayouts).reduce((a, b) => a + b, 0),
    [practiceBasedIncentivesCurrentPayouts]
  );

  const totalOutcomePotentialPayout = maximumPotentialPayout - practicePotentialPayoutTotal;

  const totalOutcomeBasedPayoutMin = Math.min(uncappedOutcomeMin, totalOutcomePotentialPayout);
  const totalOutcomeBasedPayoutMax = Math.min(uncappedOutcomeMax, totalOutcomePotentialPayout);

  const totalPayoutMin = totalOutcomeBasedPayoutMin + totalPracticeBasedPayout;
  const totalPayoutMax = totalOutcomeBasedPayoutMax + totalPracticeBasedPayout;

  const isMaxTotalPayoutReached = totalPayoutMax >= maximumPotentialPayout;

  const isMaxOutcomePayoutReached = totalOutcomeBasedPayoutMax >= totalOutcomePotentialPayout;

  // hide panel when there are no outcomes or projectAreaHa is falsy (0, null, or undefined)
  const hideInventoryPayouts = !(singleFieldOutcomes.length && projectAreaHa > 0);

  // practice based payout

  const incentivesCurrentPayouts = useMemo(
    () => aggregateIncentiveCurrentPayouts(singleFieldOutcomes, incentivesPotentialPayouts),
    [singleFieldOutcomes, incentivesPotentialPayouts]
  );

  const allPracticesMaxedOut = useMemo(() => {
    if (!incentivesPotentialPayouts?.practicePotentialTotalPayout) return false;
    return Object.entries(incentivesCurrentPayouts).every(
      ([currentPayoutkey, currentPayoutValue]) =>
        incentivesPotentialPayouts?.practicePotentialIncentives[currentPayoutkey as IncentiveName]?.total <= currentPayoutValue
    );
  }, [incentivesCurrentPayouts, incentivesPotentialPayouts?.practicePotentialIncentives, incentivesPotentialPayouts?.practicePotentialTotalPayout]);

  const isPracticePotentialFullyUtilized = allPracticesMaxedOut || practicePotentialPayoutTotal <= totalPracticeBasedPayout;

  return {
    projectAreaHa,
    maximumIncentivePerHectare,
    maximumPotentialPayout,
    totalOutcomeBasedPayoutMin,
    totalOutcomeBasedPayoutMax,
    totalPracticeBasedPayout,
    totalPayoutMin,
    totalPayoutMax,
    isMaxTotalPayoutReached,
    totalOutcomePotentialPayout,
    isMaxOutcomePayoutReached,
    hideInventoryPayouts,
    incentivesCurrentPayouts,
    allPracticesMaxedOut,
    isPracticePotentialFullyUtilized,
    practicePotentialPayoutTotal,
    incentivesPotentialPayouts,
    displayPerHectareOrTotalFarm,
  };
}
